/**
 * IndexedDB wrapper
 *
 * Object stores for charts, palm readings and settings, with a version migration
 * path, and a localStorage fallback for private-mode browsers where IndexedDB
 * throws.
 *
 * ⚠ **Every read and write is wrapped, and the reason is specific.** In a Firefox
 * private window `indexedDB.open` throws synchronously; in Safari with site data
 * blocked it resolves and then errors on the first transaction; in a Chrome
 * incognito session it works but is discarded on close. An unhandled rejection in
 * any of those paths takes the whole page down, and a site whose entire value is
 * computing a chart must not go blank because it could not *save* one.
 *
 * ◆ So the contract is: storage failure degrades to "nothing is saved", loudly in
 * the return value and visibly in the UI, never to a broken page. `available()`
 * tells the caller which mode it is in, and every function works in both.
 *
 * © Manish Jagdish Thatte
 */

import { DB_NAME, DB_VERSION, STORAGE_PREFIX } from '../config.js';

export const STORES = Object.freeze({
  charts: 'charts',
  readings: 'readings',
  settings: 'settings'
});

/** 'indexeddb' once opened, 'localstorage' after a fallback, 'none' if neither works. */
let mode = null;
let dbPromise = null;

/**
 * Open the database, or decide we cannot.
 *
 * ◆ Resolves rather than rejects on failure. A caller asking "can I save?" wants
 * an answer, not an exception to handle at every call site.
 */
function open() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    let request;
    try {
      if (typeof indexedDB === 'undefined') {
        mode = localStorageWorks() ? 'localstorage' : 'none';
        return resolve(null);
      }
      // ⚠ Throws synchronously in a Firefox private window.
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      mode = localStorageWorks() ? 'localstorage' : 'none';
      return resolve(null);
    }

    request.onupgradeneeded = (event) => {
      const db = request.result;
      migrate(db, event.oldVersion, event.newVersion ?? DB_VERSION);
    };
    request.onsuccess = () => {
      mode = 'indexeddb';
      // A later version elsewhere (another tab upgraded) closes this one.
      request.result.onversionchange = () => {
        request.result.close();
        dbPromise = null;
        mode = null;
      };
      resolve(request.result);
    };
    request.onerror = () => {
      mode = localStorageWorks() ? 'localstorage' : 'none';
      resolve(null);
    };
    request.onblocked = () => {
      mode = localStorageWorks() ? 'localstorage' : 'none';
      resolve(null);
    };
  });

  return dbPromise;
}

/**
 * Create or upgrade the stores.
 *
 * ⚠ Migrations are additive and must stay so. A saved chart carries its own
 * `schemaVersion` and the settings that produced it, so an old record is
 * *readable* rather than needing conversion — which means a migration here should
 * never rewrite or drop user data. Someone's grandmother's chart is not a cache.
 */
function migrate(db, oldVersion, newVersion) {
  if (!db.objectStoreNames.contains(STORES.charts)) {
    const charts = db.createObjectStore(STORES.charts, { keyPath: 'id' });
    charts.createIndex('savedAt', 'savedAt');
    charts.createIndex('name', 'name');
  }
  if (!db.objectStoreNames.contains(STORES.readings)) {
    const readings = db.createObjectStore(STORES.readings, { keyPath: 'id' });
    readings.createIndex('savedAt', 'savedAt');
  }
  if (!db.objectStoreNames.contains(STORES.settings)) {
    db.createObjectStore(STORES.settings, { keyPath: 'key' });
  }
  // Future versions add branches here keyed on oldVersion. Nothing is deleted.
  void oldVersion; void newVersion;
}

function localStorageWorks() {
  try {
    const probe = `${STORAGE_PREFIX}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * What storage is available: `{ mode, persistent, reason }`.
 *
 * ◆ The UI shows this. "Saved on this device" and "nothing will be saved" are very
 * different promises and the user is entitled to know which one they have before
 * they spend ten minutes entering birth data.
 */
export async function available() {
  await open();
  return {
    mode: mode ?? 'none',
    persistent: mode === 'indexeddb',
    reason: mode === 'indexeddb'
      ? null
      : mode === 'localstorage'
        ? 'IndexedDB is unavailable — most likely a private window. Charts are kept only ' +
          'for this browser session and in a smaller amount of space.'
        : 'This browser is blocking local storage, so nothing can be saved. Everything still ' +
          'computes, and a chart can be printed or exported.'
  };
}

function lsKey(store, id) {
  return `${STORAGE_PREFIX}.${store}.${id}`;
}

/** Put a record. Returns `{ ok, mode, error }` and never throws. */
export async function put(store, record) {
  const db = await open();
  if (db) {
    try {
      await tx(db, store, 'readwrite', (s) => s.put(record));
      return { ok: true, mode };
    } catch (err) {
      return { ok: false, mode, error: err.message };
    }
  }
  if (mode === 'localstorage') {
    try {
      localStorage.setItem(lsKey(store, record.id), JSON.stringify(record));
      return { ok: true, mode };
    } catch (err) {
      // Quota exceeded is the common one here, and it is worth naming.
      return { ok: false, mode, error: `local storage is full or blocked (${err.name})` };
    }
  }
  return { ok: false, mode: 'none', error: 'no storage is available' };
}

/** Get one record by id, or null. Never throws. */
export async function get(store, id) {
  const db = await open();
  if (db) {
    try {
      return await tx(db, store, 'readonly', (s) => s.get(id));
    } catch {
      return null;
    }
  }
  if (mode === 'localstorage') {
    try {
      const raw = localStorage.getItem(lsKey(store, id));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Every record in a store, newest first where a savedAt exists. Never throws. */
export async function all(store) {
  const db = await open();
  if (db) {
    try {
      const rows = await tx(db, store, 'readonly', (s) => s.getAll());
      return sortNewestFirst(rows ?? []);
    } catch {
      return [];
    }
  }
  if (mode === 'localstorage') {
    try {
      const prefix = `${STORAGE_PREFIX}.${store}.`;
      const rows = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
          try { rows.push(JSON.parse(localStorage.getItem(key))); } catch { /* skip a corrupt row */ }
        }
      }
      return sortNewestFirst(rows);
    } catch {
      return [];
    }
  }
  return [];
}

function sortNewestFirst(rows) {
  return rows.sort((a, b) => String(b?.savedAt ?? '').localeCompare(String(a?.savedAt ?? '')));
}

/** Delete one record. Returns `{ ok }`. Never throws. */
export async function remove(store, id) {
  const db = await open();
  if (db) {
    try {
      await tx(db, store, 'readwrite', (s) => s.delete(id));
      return { ok: true, mode };
    } catch (err) {
      return { ok: false, mode, error: err.message };
    }
  }
  if (mode === 'localstorage') {
    try {
      localStorage.removeItem(lsKey(store, id));
      return { ok: true, mode };
    } catch (err) {
      return { ok: false, mode, error: err.message };
    }
  }
  return { ok: false, mode: 'none', error: 'no storage is available' };
}

/**
 * Delete everything this site has stored.
 *
 * ⚠ Irreversible, and the settings page must confirm before calling it. It is
 * offered because a site that keeps birth data on someone's device owes them a
 * way to remove all of it in one action, without hunting through browser menus.
 */
export async function clearAll() {
  const results = [];
  const db = await open();
  if (db) {
    for (const store of Object.values(STORES)) {
      try {
        await tx(db, store, 'readwrite', (s) => s.clear());
        results.push({ store, ok: true });
      } catch (err) {
        results.push({ store, ok: false, error: err.message });
      }
    }
  }
  try {
    const prefix = `${STORAGE_PREFIX}.`;
    for (const key of Object.keys(localStorage).filter((k) => k.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  } catch { /* nothing to clear */ }
  return results;
}

/** Promisify one IndexedDB transaction. */
function tx(db, store, mode_, run) {
  return new Promise((resolve, reject) => {
    let request;
    try {
      const transaction = db.transaction(store, mode_);
      request = run(transaction.objectStore(store));
      transaction.onabort = () => reject(transaction.error ?? new Error('transaction aborted'));
    } catch (err) {
      return reject(err);
    }
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('request failed'));
  });
}

/**
 * A stable id for a saved chart.
 *
 * ◆ Derived from the birth data and the settings, not random, so saving the same
 * birth twice updates one record instead of accumulating near-duplicates — and so
 * the same chart on two devices has the same id when export/import lands.
 */
export function chartId({ jdUT, lat, lonEast, ayanamsa, houseSystem, nodeMode, timeKnown }) {
  const parts = [
    Number(jdUT).toFixed(8),
    lat == null ? 'na' : Number(lat).toFixed(5),
    lonEast == null ? 'na' : Number(lonEast).toFixed(5),
    ayanamsa, houseSystem, nodeMode, timeKnown ? 't' : 'f'
  ];
  return `c_${hash(parts.join('|'))}`;
}

/** FNV-1a. Not a cryptographic hash and not used as one — just a short stable key. */
function hash(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}
